import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { alertService, type Alert } from '../services/alertService';
import type { AlertDelivery } from '@catalyst-iq/shared';

type AlertWithDeliveries = Alert & {
  alert_deliveries?: AlertDelivery[];
};

interface AlertContextValue {
  alerts: AlertWithDeliveries[];
  isLoading: boolean;
  refreshAlerts: (showLoading?: boolean) => Promise<void>; // מעודכן: תומך בפרמטר טעינה
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<AlertWithDeliveries[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const previousAlertsRef = useRef<AlertWithDeliveries[]>([]);
  const processedDeliveryIdsRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef(true);
  // עדכון: מקבל פרמטר האם להציג את מצב הטעינה הכללי (ברירת מחדל היא לא להציג כדי למנוע הבהובים)
  const refreshAlerts = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setIsLoading(true);
    }

    try {
      const fetchedAlerts = (await alertService.fetchAlerts()) as AlertWithDeliveries[];
      const currentAlerts = Array.isArray(fetchedAlerts) ? fetchedAlerts : [];
      const hadPreviousSnapshot = previousAlertsRef.current.length > 0;

      if (hadPreviousSnapshot) {
        // מיפוי סטטוס קודם לפי delivery id — כדי לזהות מעבר pending → sent
        // (ולא רק delivery חדש לגמרי; אחרי התיקון אותם IDs מתעדכנים בטריגר)
        const previousDeliveryStatus = new Map<string, string>(
          previousAlertsRef.current.flatMap((alert) => {
            const deliveries = Array.isArray(alert.alert_deliveries) ? alert.alert_deliveries : [];
            return deliveries.map((delivery) => [delivery.id, String(delivery.status || '')]);
          })
        );

        const toastedAlertIds = new Set<string>();

        for (const alert of currentAlerts) {
          if (toastedAlertIds.has(alert.id)) continue;

          const deliveries = Array.isArray(alert.alert_deliveries) ? alert.alert_deliveries : [];
          const newlySent = deliveries.some((delivery) => {
            const channel = delivery.channel?.toLowerCase?.() || '';
            if (channel !== 'in_app' && channel !== 'telegram') return false;
            if (delivery.status !== 'sent') return false;

            const previousStatus = previousDeliveryStatus.get(delivery.id);
            const transitionedToSent = previousStatus !== 'sent';
            const alreadyToasted = processedDeliveryIdsRef.current.has(delivery.id);

            return transitionedToSent && !alreadyToasted;
          });

          if (!newlySent) continue;

          // מסמנים את כל ה-sent deliveries של האלרט כדי לא לכפול טוסט (telegram + in_app)
          for (const delivery of deliveries) {
            if (delivery.status === 'sent') {
              processedDeliveryIdsRef.current.add(delivery.id);
            }
          }
          toastedAlertIds.add(alert.id);

          if (!isFirstLoadRef.current) {
            const channels = deliveries
              .filter((d) => d.status === 'sent')
              .map((d) => d.channel)
              .filter(Boolean);
            const viaTelegram = channels.some((c) => String(c).toLowerCase() === 'telegram');

            toast.success(`Alert triggered: ${alert.ticker}`, {
              description: viaTelegram
                ? `Target ${alert.target_value} • sent to Telegram`
                : `Target ${alert.target_value}`,
              duration: 5000,
            });
          }
        }
      }

      // 3. בסיום הריצה הראשונה, נסמן שהטעינה הסתיימה
      if (isFirstLoadRef.current && currentAlerts.length > 0) {
        isFirstLoadRef.current = false;
      }

      previousAlertsRef.current = currentAlerts;
      setAlerts(currentAlerts);
    } catch (error) {
      console.error('Failed to refresh alerts:', error);
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // בטעינה הראשונית בלבד - נציג את מסך הטעינה (true)
    void refreshAlerts(true);

    const intervalId = window.setInterval(() => {
      // בפולינג השוטף כל 30 שניות - עדכון שקט לחלוטין ברקע (false)
      void refreshAlerts(false);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [refreshAlerts]);

  const value = useMemo<AlertContextValue>(
    () => ({
      alerts,
      isLoading,
      refreshAlerts,
    }),
    [alerts, isLoading, refreshAlerts]
  );

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
}

export function useAlerts() {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }

  return context;
}